/**
 * PaymentGuidance
 *
 * Renders the server-resolved fee guidance (design-patient-payment-guidance §4.6, §4.13.2)
 * on the referral landing, direct booking, self-referral completion and dashboard surfaces.
 * No loading / error / retry state: the payload either carries guidance or it does not.
 *
 * @module components/payment-guidance/PaymentGuidance
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import type { PaymentGuidance as PaymentGuidanceValue } from '@/types/paymentGuidance';

export interface PaymentGuidanceProps {
  /** Server-resolved guidance; null renders nothing (design §4.6). */
  guidance: PaymentGuidanceValue | null;
  /** Dashboard only: tighter padding, lead one size down. */
  compact?: boolean;
}

export const PaymentGuidance: React.FC<PaymentGuidanceProps> = ({ guidance, compact = false }) => {
  const { isDark } = useTheme();
  if (!guidance) return null;
  const body = cn('text-sm', isDark ? 'text-secondary-200' : 'text-secondary-700');
  return (
    <Card variant="bordered" shadow="none" className={cn(compact ? 'p-4' : 'p-5', 'text-left')}>
      <p className="text-xs font-medium uppercase tracking-wider text-secondary-300">{guidance.heading}</p>
      <p className={cn('mt-2 font-semibold leading-snug', compact ? 'text-base' : 'text-lg', isDark ? 'text-white' : 'text-secondary-900')}>
        {guidance.lead}
      </p>
      {guidance.body.length > 0 && (
        <div className="mt-3 space-y-2">
          {guidance.body.map((block, i) =>
            block.kind === 'bullets' ? (
              <ul key={i} className={cn('list-disc space-y-1 pl-5', body)}>
                {(block.items ?? []).map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            ) : (
              <p key={i} className={body}>{block.text}</p>
            )
          )}
        </div>
      )}
      {guidance.footnote && (
        <p className={cn('mt-3 border-t pt-3 text-xs', isDark ? 'border-secondary-700 text-secondary-300' : 'border-secondary-100 text-secondary-300')}>
          {guidance.footnote}
        </p>
      )}
    </Card>
  );
};

export default PaymentGuidance;
